import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Typography as T, ContentLoader, palette } from '@dev/ee-components';
import { convertToLocalTime } from '@dev/ee-tools';

import styled from 'styled-components';

import useMobile from '@/services/hooks/useMobile';

import { getEmailsStore } from '@/store/emails/actions';

type HeaderLineProps = {
    isMobile?: boolean;
};

const HeaderLine = styled.div<HeaderLineProps>`
    display: ${(props) => (!props.isMobile ? 'flex' : 'block')};
    ${(props) => (!props.isMobile ? `align-items: center;` : '')}
    word-break: break-all;
    padding: 8px 0;
    border-bottom: 1px solid #edf2f7;
`;

const Time = styled.span`
    color: ${palette.gray[5]};
    font-size: 11px;
    font-family: 'Mulish';
`;

const HeaderTitleWrapper = styled.span<HeaderLineProps>`
    width: 170px;
    display: inline-flex;
    align-items: center;
    ${(props) => (props.isMobile ? `padding-bottom: 10px;` : '')}
`;

const SingleEmailRow = (props: {
    headlineTitle: string;
    headlineValue?: string;
    children?: JSX.Element;
    isLoading: boolean;
    isMobile: boolean;
}): JSX.Element => (
    <HeaderLine isMobile={props.isMobile}>
        <HeaderTitleWrapper isMobile={props.isMobile}>
            <T variant="body2">{props.headlineTitle}</T>
        </HeaderTitleWrapper>
        {props.isLoading ? (
            <ContentLoader mode="row" height={18} width={170} />
        ) : props.children ? (
            props.children
        ) : (
            <T variant="caption" display="inline">
                {props.headlineValue}
            </T>
        )}
    </HeaderLine>
);

const EmailInfoRows = (): JSX.Element => {
    const { t } = useTranslation();
    const { email, emailLoading, msgId } = useSelector(getEmailsStore);
    const isMobile = useMobile();

    return (
        <div>
            <SingleEmailRow
                isLoading={emailLoading}
                headlineTitle={t('from')}
                headlineValue={email?.Status?.FromEmail}
                isMobile={isMobile}
            />
            <SingleEmailRow
                isLoading={emailLoading}
                headlineTitle={t('to')}
                headlineValue={email?.Status?.ToEmail}
                isMobile={isMobile}
            />
            <SingleEmailRow
                isLoading={emailLoading}
                headlineTitle={t('subject')}
                headlineValue={email?.Preview?.Subject}
                isMobile={isMobile}
            />
            <SingleEmailRow
                isLoading={emailLoading}
                headlineTitle={t('messageid')}
                headlineValue={msgId}
                isMobile={isMobile}
            />
            <SingleEmailRow
                isLoading={emailLoading}
                headlineTitle={t('channel')}
                headlineValue={email?.Status?.Channel}
                isMobile={isMobile}
            />
            <SingleEmailRow
                isLoading={emailLoading}
                headlineTitle={t('sentfrom')}
                headlineValue={email?.Status?.SentFrom}
                isMobile={isMobile}
            />
            <SingleEmailRow isLoading={emailLoading} headlineTitle={t('sent')} isMobile={isMobile}>
                <T variant="caption">
                    {email?.Status?.SentDate && (
                        <>
                            {convertToLocalTime(email?.Status?.SentDate, 'L')}{' '}
                            <Time>{convertToLocalTime(email?.Status?.SentDate, 'LT')}</Time>
                        </>
                    )}
                </T>
            </SingleEmailRow>
            <SingleEmailRow
                isLoading={emailLoading}
                headlineTitle={t('envelopefrom')}
                headlineValue={email?.Status?.EnvelopeFrom}
                isMobile={isMobile}
            />
        </div>
    );
};

export default EmailInfoRows;
